import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Button,
  HStack,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { CheckIcon, RepeatIcon } from '@chakra-ui/icons';

const SHAPES = ['circle', 'square', 'triangle'];

// Shape labels with friendly descriptions
const SHAPE_LABELS = {
  circle: 'Circle',
  square: 'Square',
  triangle: 'Triangle',
};

// Generate a target shape outline for the guide
function drawGuideShape(ctx, shape, w, h) {
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();

  const cx = w / 2;
  const cy = h / 2;
  const size = Math.min(w, h) * 0.35;

  if (shape === 'circle') {
    ctx.arc(cx, cy, size, 0, Math.PI * 2);
  } else if (shape === 'square') {
    ctx.rect(cx - size, cy - size, size * 2, size * 2);
  } else if (shape === 'triangle') {
    ctx.moveTo(cx, cy - size);
    ctx.lineTo(cx + size, cy + size);
    ctx.lineTo(cx - size, cy + size);
    ctx.closePath();
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

// ----- Shape Recognition Algorithms -----

function analyzePoints(points) {
  if (points.length < 10) return { shape: null, confidence: 0 };

  // Compute centroid
  let sumX = 0, sumY = 0;
  for (const p of points) { sumX += p.x; sumY += p.y; }
  const cx = sumX / points.length;
  const cy = sumY / points.length;

  // Distances from centroid
  const dists = points.map(p => Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2));
  const avgDist = dists.reduce((a, b) => a + b, 0) / dists.length;
  const distVariance = dists.reduce((a, d) => a + (d - avgDist) ** 2, 0) / dists.length;
  const distStdDev = Math.sqrt(distVariance);
  const circularityRatio = distStdDev / (avgDist || 1);

  // Compute angles and find corners (large direction changes)
  const corners = findCorners(points);

  // Check closedness: distance between first and last point relative to avg radius
  const closedness = Math.sqrt(
    (points[0].x - points[points.length - 1].x) ** 2 +
    (points[0].y - points[points.length - 1].y) ** 2
  ) / (avgDist || 1);

  // Not closed enough? Low confidence for all shapes
  const closedPenalty = closedness > 0.8 ? 0.5 : 1.0;

  // --- Circle detection ---
  // Low variance in distance from centroid = circle-like
  const circleScore = Math.max(0, 1 - circularityRatio * 3) * closedPenalty;

  // --- Square detection ---
  // Should have ~4 corners, roughly equal side lengths
  const squareCornerScore = corners.length >= 3 && corners.length <= 6
    ? 1 - Math.abs(corners.length - 4) * 0.2
    : 0;
  const squareScore = squareCornerScore * 0.7 * closedPenalty +
    (circularityRatio > 0.1 ? 0.3 : 0) * closedPenalty;

  // --- Triangle detection ---
  const triangleCornerScore = corners.length >= 2 && corners.length <= 5
    ? 1 - Math.abs(corners.length - 3) * 0.25
    : 0;
  const triangleScore = triangleCornerScore * 0.7 * closedPenalty +
    (circularityRatio > 0.08 ? 0.3 : 0) * closedPenalty;

  const scores = { circle: circleScore, square: squareScore, triangle: triangleScore };
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];

  return { shape: best[0], confidence: best[1], scores };
}

function findCorners(points, angleDegThreshold = 40) {
  if (points.length < 5) return [];

  // Smooth the points a bit by sampling every Nth
  const step = Math.max(1, Math.floor(points.length / 60));
  const sampled = [];
  for (let i = 0; i < points.length; i += step) {
    sampled.push(points[i]);
  }
  if (sampled.length < 5) return [];

  const corners = [];
  const windowSize = Math.max(2, Math.floor(sampled.length / 12));

  for (let i = windowSize; i < sampled.length - windowSize; i++) {
    const prev = sampled[i - windowSize];
    const curr = sampled[i];
    const next = sampled[i + windowSize];

    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;

    const angle1 = Math.atan2(dy1, dx1);
    const angle2 = Math.atan2(dy2, dx2);
    let diff = Math.abs(angle2 - angle1);
    if (diff > Math.PI) diff = 2 * Math.PI - diff;

    if (diff > (angleDegThreshold * Math.PI / 180)) {
      // Don't double-count nearby corners
      if (corners.length === 0 ||
          Math.sqrt((curr.x - corners[corners.length - 1].x) ** 2 +
                    (curr.y - corners[corners.length - 1].y) ** 2) > 15) {
        corners.push(curr);
      }
    }
  }
  return corners;
}

// ----------------------------------

const ShapeCaptcha = ({ onVerified, isInvalid }) => {
  const canvasRef = useRef(null);
  const [targetShape, setTargetShape] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState([]);
  const [result, setResult] = useState(null); // { passed, detected }
  const [verified, setVerified] = useState(false);

  // Pick a random shape on mount
  useEffect(() => {
    pickNewShape();
  }, []);

  const pickNewShape = useCallback(() => {
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    setTargetShape(shape);
    setResult(null);
    setVerified(false);
    setPoints([]);
    onVerified(false);
    clearCanvas();
  }, [onVerified]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw guide
    drawGuideShape(ctx, targetShape, canvas.width, canvas.height);
  }, [targetShape]);

  // Redraw guide when target changes
  useEffect(() => {
    clearCanvas();
  }, [targetShape, clearCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    if (verified) return;
    e.preventDefault();
    // Reset on new draw attempt
    setResult(null);
    setPoints([]);
    clearCanvas();

    const pos = getPos(e);
    setIsDrawing(true);
    setPoints([pos]);

    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.strokeStyle = '#63B3ED'; // blue.300
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e) => {
    if (!isDrawing || verified) return;
    e.preventDefault();
    const pos = getPos(e);
    setPoints(prev => [...prev, pos]);

    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    if (!isDrawing || verified) return;
    e.preventDefault();
    setIsDrawing(false);

    // Analyze what was drawn
    if (points.length < 10) {
      setResult({ passed: false, detected: null, message: 'Draw a bigger shape — try again!' });
      return;
    }

    const analysis = analyzePoints(points);

    if (analysis.shape === targetShape && analysis.confidence >= 0.3) {
      setResult({ passed: true, detected: analysis.shape });
      setVerified(true);
      onVerified(true);
    } else {
      const msg = analysis.confidence < 0.2
        ? "Couldn't recognize a shape — try drawing more carefully."
        : `That looks more like a ${SHAPE_LABELS[analysis.shape] || 'something else'}. Try again!`;
      setResult({ passed: false, detected: analysis.shape, message: msg });
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor={isInvalid ? 'red.400' : verified ? 'green.400' : 'gray.600'}
      borderRadius="md"
      p={3}
      bg="gray.900"
    >
      <VStack spacing={2} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="sm" fontWeight="bold" color="gray.300">
            Bot Check — Draw a{' '}
            <Text as="span" color="orange.300" fontWeight="extrabold">
              {SHAPE_LABELS[targetShape]}
            </Text>
          </Text>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            leftIcon={<RepeatIcon />}
            onClick={pickNewShape}
          >
            New Shape
          </Button>
        </HStack>

        <Box
          position="relative"
          borderRadius="md"
          overflow="hidden"
          bg="gray.800"
          cursor={verified ? 'default' : 'crosshair'}
        >
          <canvas
            ref={canvasRef}
            width={280}
            height={180}
            style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {verified && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="rgba(0,0,0,0.4)"
            >
              <HStack>
                <Icon as={CheckIcon} color="green.300" boxSize={5} />
                <Text color="green.300" fontWeight="bold" fontSize="md">Verified!</Text>
              </HStack>
            </Box>
          )}
        </Box>

        {result && !result.passed && (
          <Text fontSize="xs" color="red.300" textAlign="center">
            {result.message}
          </Text>
        )}

        {isInvalid && !verified && !result && (
          <Text fontSize="xs" color="red.400" textAlign="center">
            Please complete the bot check above
          </Text>
        )}

        <Text fontSize="2xs" color="gray.600" textAlign="center">
          Draw the shape with your mouse or finger to prove you're human
        </Text>
      </VStack>
    </Box>
  );
};

export default ShapeCaptcha;
